'use client';

import { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Locale } from '@/lib/i18n/config';
import type { Dictionary } from '@/lib/i18n/dictionaries';
import { Logo } from './logo';
import { LocaleSwitcher } from './locale-switcher';
import { Button } from '@/components/ui/button';
import { localePath } from '@/lib/i18n/helpers';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

export function Header({ locale, dict }: { locale: Locale; dict: Dictionary }) {
  const [scrolled, setScrolled] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const pathname = useLocation().pathname;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const close = useCallback(() => setSheetOpen(false), []);

  /**
   * Forces scroll-to-top + focus on <main> when the user clicks a nav link
   * that points to the route they are already on (Next.js' Link does not
   * trigger navigation in that case, so the global ScrollToTop effect never
   * fires). For real route changes this runs harmlessly before navigation.
   */
  const scrollIfSameRoute = useCallback(
    (href: string) => () => {
      if (typeof window === 'undefined') return;
      if (pathname === href) {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
        document.getElementById('main')?.focus({ preventScroll: true });
      }
    },
    [pathname]
  );

  const base = `/${locale}`;
  const links = [
    { href: base, label: dict.nav.home },
    { href: `${base}/areas`, label: dict.nav.services },
    { href: `${base}/equipo`, label: dict.nav.team },
    { href: `${base}/noticias`, label: dict.nav.news },
    { href: `${base}/preguntas`, label: dict.nav.faqs },
    { href: `${base}/contacto`, label: dict.nav.contact },
  ];

  const isActive = (href: string) =>
    href === base ? pathname === base : pathname === href || pathname.startsWith(`${href}/`);

  return (
    <header
      className={cn(
        'sticky top-0 z-40 w-full transition-all duration-300',
        scrolled
          ? 'border-b border-border bg-background/95 shadow-sm backdrop-blur-md'
          : 'bg-transparent'
      )}
    >
      <div className="container-tight flex h-20 items-center justify-between lg:h-24">
        <Link to={base} className="shrink-0" onClick={scrollIfSameRoute(base)} aria-label={dict.a11y.logoHome}>
          <Logo dict={dict} />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {links.map((l) => {
            const active = isActive(l.href);
            return (
              <Link
                key={l.href}
                to={l.href}
                onClick={scrollIfSameRoute(l.href)}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200',
                  active
                    ? 'bg-secondary text-accent'
                    : 'text-foreground/75 hover:bg-secondary/60 hover:text-accent'
                )}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <LocaleSwitcher current={locale} />
          <Button asChild size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90">
            <Link to={localePath('/solicitar', locale)}>{dict.nav.request}</Link>
          </Button>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <LocaleSwitcher current={locale} />
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <button
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border transition hover:bg-secondary"
                aria-label={sheetOpen ? dict.a11y.closeMenu : dict.a11y.openMenu}
              >
                {sheetOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[340px] max-w-[90vw] sm:w-[420px] sm:max-w-[420px]">
              <div className="mt-8 flex flex-col gap-1">
                {links.map((l) => {
                  const active = isActive(l.href);
                  return (
                    <Link
                      key={l.href}
                      to={l.href}
                      onClick={() => {
                        close();
                        scrollIfSameRoute(l.href)();
                      }}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'relative rounded-lg px-4 py-3 text-base font-medium transition',
                        active
                          ? 'bg-accent/10 text-accent before:absolute before:left-0 before:top-2 before:h-[calc(100%-1rem)] before:w-1 before:rounded-r-full before:bg-accent before:content-[""]'
                          : 'text-foreground/90 hover:bg-secondary'
                      )}
                    >
                      {l.label}
                    </Link>
                  );
                })}
                <div className="mt-4 px-4">
                  <Button asChild className="w-full bg-accent text-accent-foreground hover:bg-accent/90">
                    <Link to={localePath('/solicitar', locale)} onClick={close}>
                      {dict.nav.request}
                    </Link>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
